import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { proApply, proMe, proUpload } from "@/lib/api";
import { Upload, CheckCircle2, Clock, ShieldCheck, XCircle } from "lucide-react";

const inp = "w-full border border-line bg-surface px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none";
const TYPES = [["purohit", "Purohit / Priest"], ["astrologer", "Astrologer (Jyotishi)"], ["guru", "Guru / Acharya"], ["sme", "Subject-Matter Expert"]];

export default function ProSignup() {
  const { user } = useAuth();
  const { pick } = useLang();
  const { page } = useSettings();
  const pg = page("/join-as-purohit");
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", type: "purohit", lineage: "", languages: "", specialities: "", experience_years: "", city: "", phone: "", bio: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    proMe().then((d) => {
      if (d.profile) {
        setProfile(d.profile);
        setForm((f) => ({ ...f, name: d.profile.name || "", type: d.profile.type || "purohit", lineage: d.profile.lineage || "", languages: (d.profile.languages || []).join(", "), specialities: (d.profile.specialities || []).join(", "), experience_years: d.profile.experience_years ?? "", city: d.profile.city || "", bio: d.profile.bio || "" }));
      }
    }).catch(() => {});
  }, [user]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    setBusy(true);
    try {
      const payload = { ...form, languages: form.languages.split(",").map((x) => x.trim()).filter(Boolean), specialities: form.specialities.split(",").map((x) => x.trim()).filter(Boolean), experience_years: form.experience_years ? parseInt(form.experience_years, 10) : null };
      const p = await proApply(payload);
      setProfile({ ...(profile || {}), ...p });
      toast.success(pick("आवेदन सहेजा गया", "Application saved — now add your photo, intro video and Aadhaar"));
      const d = await proMe(); setProfile(d.profile);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const upload = async (kind, file) => {
    if (!file) return;
    try {
      const p = await proUpload(kind, file);
      setProfile(p);
      toast.success(pick("अपलोड हो गया", "Uploaded"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const StatusBanner = () => {
    const st = profile?.verification_status;
    const map = {
      verified: [CheckCircle2, "text-green-600", pick("सत्यापित — आपकी प्रोफ़ाइल प्रकाशित है", "Verified — your profile is live")],
      rejected: [XCircle, "text-red-500", pick("आवेदन अस्वीकृत", "Application not approved") + (profile?.verification_note ? ` — ${profile.verification_note}` : "")],
      pending: [Clock, "text-amber-600", pick("समीक्षाधीन — हम शीघ्र सत्यापित करेंगे", "Under review — we'll verify your documents shortly")],
    };
    const [Icon, cls, txt] = map[st] || map.pending;
    return <div className="flex items-center gap-2 border border-line bg-surface p-3 text-sm" data-testid="pro-status"><Icon className={`h-5 w-5 ${cls}`} /><span className={cls}>{txt}</span></div>;
  };

  const UploadRow = ({ kind, label, accept, done, hint }) => (
    <div className="flex items-center justify-between gap-3 border border-line bg-surface p-3">
      <div><p className="text-sm text-ink">{label}{done && <CheckCircle2 className="ml-2 inline h-4 w-4 text-green-600" />}</p><p className="text-xs text-ink-3">{hint}</p></div>
      <label className="inline-flex cursor-pointer items-center gap-1.5 border border-saffron px-3 py-1.5 text-xs font-medium text-saffron hover:bg-saffron hover:text-white" data-testid={`upload-${kind}`}>
        <Upload className="h-3.5 w-3.5" />{done ? pick("बदलें", "Replace") : pick("अपलोड", "Upload")}
        <input type="file" accept={accept} className="hidden" onChange={(e) => upload(kind, e.target.files?.[0])} />
      </label>
    </div>
  );

  return (
    <div data-testid="pro-signup-page">
      <Seo path="/join-as-purohit" title="Join Dharmartha as a Purohit, Astrologer or Guru"
        description="Offer poojas, yagyas and jyotish consultations to devotees worldwide. Apply as a verified purohit, astrologer, guru or subject-matter expert on Dharmartha." />
      <PageHero art={HERO_ART.devotion} overline={pick("सेवा में सहभागी", "Serve with us")}
        deva={pg.title?.hi || "पुरोहित पंजीकरण"}
        en={pick(pg.title?.hi || "पुरोहित / आचार्य के रूप में जुड़ें", pg.title?.en || "Join as a Purohit, Astrologer or Guru")}
        tagline={pick(pg.subtitle?.hi || "सत्यापित सेवा प्रदाता बनें और साधकों से जुड़ें।", pg.subtitle?.en || "Become a verified service provider and connect with devotees.")} />

      <section className="mx-auto max-w-2xl px-5 py-12 md:px-8">
        {!user ? (
          <div className="border border-line bg-surface p-6 text-center" data-testid="pro-login-gate">
            <p className="text-ink-2">{pick("आवेदन करने हेतु कृपया लॉगिन करें।", "Please sign in to apply.")}</p>
            <Link to="/login" className="mt-4 inline-block bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white">{pick("लॉगिन", "Sign in")}</Link>
          </div>
        ) : (
          <div className="space-y-5">
            {profile && <StatusBanner />}
            {profile?.aadhaar_verified && (
              <div className="flex items-center gap-2 border border-green-500/30 bg-green-500/5 p-3 text-sm text-green-700" data-testid="pro-aadhaar-verified">
                <ShieldCheck className="h-5 w-5" /><span>{pick("आधार सत्यापित", "Aadhaar verified by our team")}</span>
              </div>
            )}
            <div className="space-y-3 border border-line bg-surface p-5">
              <h2 className="font-serif-latin text-xl text-ink">{pick("आपका विवरण", "Your details")}</h2>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={pick("पूरा नाम", "Full name")} className={inp} data-testid="pro-name" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp} data-testid="pro-type">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <div className="grid gap-3 sm:grid-cols-2">
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={pick("नगर", "City")} className={inp} />
                <input value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} placeholder={pick("अनुभव (वर्ष)", "Experience (years)")} type="number" className={inp} />
              </div>
              <input value={form.lineage} onChange={(e) => setForm({ ...form, lineage: e.target.value })} placeholder={pick("परम्परा / गुरु परम्परा", "Lineage / Sampradaya")} className={inp} />
              <input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} placeholder={pick("भाषाएँ (अल्पविराम से अलग)", "Languages (comma separated)")} className={inp} data-testid="pro-languages" />
              <input value={form.specialities} onChange={(e) => setForm({ ...form, specialities: e.target.value })} placeholder={pick("विशेषज्ञता (अल्पविराम से अलग)", "Specialities (comma separated)")} className={inp} />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={pick("संपर्क नंबर", "Contact phone")} className={inp} />
              <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} placeholder={pick("संक्षिप्त परिचय", "Short bio")} className={inp} />
              <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white disabled:opacity-60" data-testid="pro-submit">
                <ShieldCheck className="h-4 w-4" />{profile ? pick("विवरण अद्यतन करें", "Update details") : pick("आवेदन जमा करें", "Submit application")}
              </button>
            </div>

            {profile && (
              <div className="space-y-3">
                <h2 className="font-serif-latin text-xl text-ink">{pick("दस्तावेज़ अपलोड", "Documents")}</h2>
                <UploadRow kind="photo" label={pick("प्रोफ़ाइल फ़ोटो", "Profile photo")} accept="image/*" done={!!profile.photo_url} hint="JPG / PNG, max 5MB" />
                <UploadRow kind="intro_video" label={pick("परिचय वीडियो", "Intro video")} accept="video/*" done={!!profile.intro_video_url} hint="MP4, max 60MB" />
                <UploadRow kind="aadhaar_front" label={pick("आधार कार्ड — सामने (निजी)", "Aadhaar card — front (private)")} accept="image/*,application/pdf" done={profile.aadhaar_front_uploaded} hint={pick("केवल सत्यापन हेतु — गोपनीय रखा जाता है", "For verification only — kept private, admin-access only")} />
                <UploadRow kind="aadhaar_back" label={pick("आधार कार्ड — पीछे (निजी)", "Aadhaar card — back (private)")} accept="image/*,application/pdf" done={profile.aadhaar_back_uploaded} hint={pick("केवल सत्यापन हेतु — गोपनीय रखा जाता है", "For verification only — kept private, admin-access only")} />
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
